import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, CheckCircle2, ExternalLink, FileText,
  Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Image as ImageIcon, X,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";
import RichTextEditor from "../components/RichTextEditor";

export default function AdminCustomPages() {
  const [pages, setPages] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusSlug = searchParams.get("focus");

  const jumpToSlug = (slug) => {
    if (!slug) return;
    setSearchParams({ focus: slug });
    // The PageEditor already auto-scrolls when focused (see effect inside that
    // component). Setting the param re-triggers it on every dropdown change.
  };

  const refresh = async () => {
    try {
      const r = await api.get("/admin/custom-pages");
      setPages(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/custom-pages/${slug}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]" data-testid="page-admin-custom-pages">
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <FileText size={16} strokeWidth={2.5} />
              <span className="label-kicker">Custom pages</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              Build your own pages.
            </h1>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              Each page lives at <code>/page/&lt;slug&gt;</code> with a hero,
              kicker, intro and rich-text body. Tile editors can link to any of them.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="custom-page-new"
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white"
          >
            <Plus size={14} /> New page
          </button>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="custom-pages-error"
          >
            {error}
          </p>
        ) : null}

        {creating ? (
          <CreateForm
            onCancel={() => setCreating(false)}
            onCreated={async () => {
              setCreating(false);
              await refresh();
            }}
            setError={setError}
          />
        ) : null}

        {pages === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : pages.length === 0 && !creating ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="custom-pages-empty"
          >
            <p className="font-display text-lg font-bold">No custom pages yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Click "New page" above to build your first one.
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="custom-pages-list">
            {pages.length > 1 ? (
              <div
                className="flex flex-wrap items-center gap-3 rounded-md border border-black/10 bg-[#F8FAFC] px-4 py-3"
                data-testid="custom-pages-jump-bar"
              >
                <label
                  htmlFor="custom-pages-jump"
                  className="label-kicker shrink-0 text-[var(--brand-body)]"
                >
                  Jump to page
                </label>
                <select
                  id="custom-pages-jump"
                  value={focusSlug || ""}
                  onChange={(e) => jumpToSlug(e.target.value)}
                  data-testid="custom-pages-jump-select"
                  className="min-w-[220px] flex-1 rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm font-semibold outline-none focus:border-[#0A0F17]"
                >
                  <option value="">— pick a page —</option>
                  {[...pages]
                    .sort((a, b) =>
                      (a.title || a.slug).localeCompare(b.title || b.slug, undefined, {
                        sensitivity: "base",
                      }),
                    )
                    .map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.title} (/page/{p.slug})
                      </option>
                    ))}
                </select>
                <span className="label-kicker text-[var(--brand-body)]">
                  {pages.length} total
                </span>
              </div>
            ) : null}

            {pages.map((p, i) => (
              <PageEditor
                key={p.slug}
                page={p}
                index={i}
                total={pages.length}
                onSaved={refresh}
                onDelete={() => remove(p.slug)}
                setOuterError={setError}
                focused={p.slug === focusSlug}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CreateForm({ onCancel, onCreated, setError }) {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const slugify = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

  const slugValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const submit = async (e) => {
    e.preventDefault();
    if (!slugValid || !title.trim()) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/admin/custom-pages", { slug, title: title.trim() });
      await onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-md border-2 border-[#0A0F17]/20 bg-[#F8FAFC] p-5 sm:p-6"
      data-testid="custom-page-create-form"
    >
      <p className="label-kicker text-[var(--brand-body)]">New page</p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">Title</span>
          <input
            autoFocus
            required
            maxLength={200}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (!slug) setSlug(slugify(e.target.value));
            }}
            data-testid="custom-page-create-title"
            placeholder="About Jonny"
            className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </label>
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">Slug (URL)</span>
          <input
            required
            maxLength={80}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="custom-page-create-slug"
            placeholder="about-jonny"
            className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17] ${
              slug && !slugValid ? "border-[#E63946]" : "border-black/15"
            }`}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-[var(--brand-body)]">
        Will live at <code>/page/{slug || "&lt;slug&gt;"}</code>. You can edit
        body, intro, hero etc. after creating it.
      </p>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!slugValid || !title.trim() || saving}
          data-testid="custom-page-create-submit"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Creating…" : "Create page"}
        </button>
      </div>
    </form>
  );
}

function PageEditor({ page, index, total, onSaved, onDelete, setOuterError, focused }) {
  const cardRef = useRef(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(snapshotOf(page));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setForm(snapshotOf(page)); }, [page]);

  useEffect(() => {
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      cardRef.current.classList.add("ring-2", "ring-[#6CABDD]");
      const t = setTimeout(() => {
        cardRef.current?.classList.remove("ring-2", "ring-[#6CABDD]");
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [focused]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleTileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setOuterError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField("tile_image_url")(r.data.url || "");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshotOf(page));
  const slugValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug);
  const canSave = dirty && slugValid && form.title.trim().length > 0;

  const save = async (e) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError("");
    setOuterError("");
    try {
      await api.put(`/admin/custom-pages/${page.slug}`, form);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      ref={cardRef}
      onSubmit={save}
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6 transition-shadow"
      data-testid={`custom-page-${page.slug}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/5 pb-4">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${
              form.published ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-black/10 text-[var(--brand-body)]"
            }`}
          >
            {form.published ? <Eye size={10} /> : <EyeOff size={10} />}
            {form.published ? "Published" : "Draft"}
          </span>
          <div>
            <p className="font-display text-base font-bold leading-none">
              {form.title || <span className="text-[var(--brand-body)]">(untitled)</span>}
            </p>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              /page/{form.slug}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post(`/admin/custom-pages/${page.slug}/move`, { direction: "up" });
                onSaved();
              } catch (e) {
                setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              }
            }}
            disabled={index === 0}
            data-testid={`custom-page-${page.slug}-move-up`}
            title="Move up"
            className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post(`/admin/custom-pages/${page.slug}/move`, { direction: "down" });
                onSaved();
              } catch (e) {
                setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              }
            }}
            disabled={index === total - 1}
            data-testid={`custom-page-${page.slug}-move-down`}
            title="Move down"
            className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowDown size={14} />
          </button>
          <a
            href={`/page/${page.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
            data-testid={`custom-page-${page.slug}-preview`}
          >
            Preview <ExternalLink size={12} />
          </a>
          <RouterLink
            to={`/admin/page-tiles?page_key=page:${page.slug}`}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 label-kicker text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
            data-testid={`custom-page-${page.slug}-tiles`}
          >
            Manage tiles →
          </RouterLink>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/30 px-2 py-1 label-kicker text-[#E63946] hover:bg-[#E63946]/10"
            data-testid={`custom-page-${page.slug}-delete`}
          >
            <Trash2 size={10} /> Delete
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Text label="Title" value={form.title} onChange={setField("title")} maxLength={200} testid={`custom-${page.slug}-title`} />
        <Text label="Slug (URL)" value={form.slug} onChange={setField("slug")} maxLength={80} hint="Lowercase letters, numbers and dashes." invalid={!slugValid} testid={`custom-${page.slug}-slug`} />
      </div>

      <div className="mt-5">
        <Text label="Kicker (small label above title)" value={form.kicker} onChange={setField("kicker")} maxLength={80} testid={`custom-${page.slug}-kicker`} />
      </div>

      <div className="mt-5">
        <label className="label-kicker text-[var(--brand-body)]">Intro</label>
        <textarea
          rows={2}
          value={form.intro}
          onChange={(e) => setField("intro")(e.target.value)}
          maxLength={600}
          data-testid={`custom-${page.slug}-intro`}
          className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          placeholder="A line or two under the title."
        />
      </div>

      <div className="mt-5">
        <label className="label-kicker text-[var(--brand-body)]">
          Tile blurb · {(form.tile_blurb || "").length}/140 (optional)
        </label>
        <textarea
          rows={2}
          value={form.tile_blurb || ""}
          onChange={(e) => setField("tile_blurb")(e.target.value)}
          maxLength={140}
          data-testid={`custom-${page.slug}-tile-blurb`}
          className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          placeholder="Shown under this page's name on the homepage Explore tile. Leave blank to reuse the intro."
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <ColorPicker
          label="Kicker colour"
          hint="Blank = default brand accent."
          value={form.kicker_color}
          onChange={setField("kicker_color")}
          testid={`custom-${page.slug}-kicker-color`}
        />
        <ColorPicker
          label="Title colour"
          hint="Blank = default ink colour."
          value={form.title_color}
          onChange={setField("title_color")}
          testid={`custom-${page.slug}-title-color`}
        />
      </div>

      <label className="mt-5 flex items-center gap-2 rounded-md border border-black/10 bg-[#F8FAFC] px-3 py-2 text-sm">
        <input
          type="checkbox"
          checked={!!form.hide_hero}
          onChange={(e) => setField("hide_hero")(e.target.checked)}
          data-testid={`custom-${page.slug}-hide-hero`}
          className="h-4 w-4"
        />
        <span>
          <span className="font-semibold">Hide the hero on /page/{form.slug}.</span>{" "}
          <span className="text-[var(--brand-body)]">
            Skip the title block so the body starts immediately under the site header.
          </span>
        </span>
      </label>

      <div className="mt-6">
        <label className="label-kicker text-[var(--brand-body)]">Body (rich text)</label>
        <div className="mt-2">
          <RichTextEditor
            value={form.body}
            onChange={setField("body")}
            placeholder="Start writing this page…"
          />
        </div>
      </div>

      <div className="mt-6 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <p className="label-kicker text-[var(--brand-body)]">
          When the body is empty
        </p>
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          Shown in the dashed box if you save this page without any body
          content. Clear both fields to hide the box completely.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="label-kicker text-[var(--brand-body)]">
              Empty-state title · {form.empty_title.length}/160
            </span>
            <input
              type="text"
              value={form.empty_title}
              onChange={(e) => setField("empty_title")(e.target.value)}
              maxLength={160}
              data-testid={`custom-${page.slug}-empty-title`}
              placeholder="Nothing here yet."
              className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm font-bold outline-none focus:border-[#0A0F17]"
            />
          </label>
          <label className="block">
            <span className="label-kicker text-[var(--brand-body)]">
              Empty-state body · {form.empty_body.length}/400
            </span>
            <textarea
              rows={2}
              value={form.empty_body}
              onChange={(e) => setField("empty_body")(e.target.value)}
              maxLength={400}
              data-testid={`custom-${page.slug}-empty-body`}
              placeholder="The Sky Blue brain trust is on it. Check back soon."
              className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0A0F17]"
            />
          </label>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4 sm:grid-cols-2">
        <ToggleRow
          label="Published"
          hint="Off = draft, only you can preview it."
          checked={form.published}
          onChange={(v) => setField("published")(v)}
          testid={`custom-${page.slug}-published`}
        />
        <ToggleRow
          label="Show in header nav"
          hint="Adds a link in the main nav bar."
          checked={form.show_in_nav}
          onChange={(v) => setField("show_in_nav")(v)}
          testid={`custom-${page.slug}-shownav`}
        />
        {form.show_in_nav ? (
          <div className="sm:col-span-2 space-y-3">
            <div>
              <label className="label-kicker text-[var(--brand-body)]">Nav label (optional)</label>
              <input
                type="text"
                value={form.nav_label}
                onChange={(e) => setField("nav_label")(e.target.value)}
                maxLength={40}
                data-testid={`custom-${page.slug}-navlabel`}
                placeholder="Defaults to the page title"
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>

            {/* Home-tile customisation — same shape as nav_items. Used by
                the rotating Explore tiles on the home page when this
                custom page happens to be picked. */}
            <div
              className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-3"
              data-testid={`custom-${page.slug}-tile-settings`}
            >
              <span className="label-kicker text-[var(--brand-body)]">Home tile</span>
              <div
                className="h-12 w-20 flex-shrink-0 overflow-hidden rounded-sm border border-black/10 bg-cover bg-center"
                style={{
                  backgroundColor: form.tile_color || "#002d75",
                  backgroundImage: form.tile_image_url
                    ? `url(${resolveAsset(form.tile_image_url)})`
                    : undefined,
                }}
                aria-label="Tile preview"
                data-testid={`custom-${page.slug}-tile-preview`}
              />
              <label className="flex items-center gap-1.5">
                <span className="label-kicker text-[var(--brand-body)]">Colour</span>
                <input
                  type="color"
                  value={form.tile_color || "#002d75"}
                  onChange={(e) => setField("tile_color")(e.target.value)}
                  data-testid={`custom-${page.slug}-tile-color`}
                  className="h-8 w-10 cursor-pointer rounded-md border border-black/15 bg-white"
                />
                {form.tile_color ? (
                  <button
                    type="button"
                    onClick={() => setField("tile_color")("")}
                    data-testid={`custom-${page.slug}-tile-color-clear`}
                    className="text-xs text-[var(--brand-body)] underline-offset-2 hover:underline"
                  >
                    clear
                  </button>
                ) : null}
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleTileImageUpload}
                  data-testid={`custom-${page.slug}-tile-image-input`}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid={`custom-${page.slug}-tile-image-upload`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <ImageIcon size={12} />
                  )}
                  {form.tile_image_url ? "Replace image" : "Upload image"}
                </button>
                {form.tile_image_url ? (
                  <button
                    type="button"
                    onClick={() => setField("tile_image_url")("")}
                    data-testid={`custom-${page.slug}-tile-image-clear`}
                    title="Remove image"
                    className="inline-flex items-center rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs hover:border-[#E63946] hover:text-[#E63946]"
                  >
                    <X size={12} />
                  </button>
                ) : null}
              </div>
              <span className="ml-auto text-xs text-[var(--brand-body)]">
                Shown on the home page Explore tile.
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <p
          className="mt-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
          data-testid={`custom-${page.slug}-error`}
        >
          {error}
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-black/5 pt-4">
        {savedFlash ? (
          <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
        <button
          type="submit"
          disabled={!canSave || saving}
          data-testid={`custom-${page.slug}-save`}
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function snapshotOf(page) {
  return {
    title: page.title || "",
    slug: page.slug,
    kicker: page.kicker || "",
    intro: page.intro || "",
    tile_blurb: page.tile_blurb || "",
    body: page.body || "",
    kicker_color: page.kicker_color || "",
    title_color: page.title_color || "",
    hide_hero: !!page.hide_hero,
    empty_title: page.empty_title ?? "Nothing here yet.",
    empty_body:
      page.empty_body ?? "The Sky Blue brain trust is on it. Check back soon.",
    published: !!page.published,
    show_in_nav: !!page.show_in_nav,
    nav_label: page.nav_label || "",
    tile_image_url: page.tile_image_url || "",
    tile_color: page.tile_color || "",
  };
}

function ColorPicker({ label, hint, value, onChange, testid }) {
  const safe = value || "";
  const valid = !safe || /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(safe);
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          value={safe || "#0A0F17"}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-swatch`}
          className="h-10 w-14 cursor-pointer rounded-md border border-black/15 bg-white p-1"
        />
        <input
          type="text"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0A0F17"
          maxLength={7}
          data-testid={testid}
          className={`block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17] ${
            valid ? "border-black/15" : "border-[#E63946]"
          }`}
        />
        {safe ? (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="label-kicker shrink-0 text-[var(--brand-body)] hover:text-[#0A0F17]"
            aria-label="Use default colour"
          >
            Clear
          </button>
        ) : null}
      </div>
      {hint ? <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p> : null}
    </label>
  );
}


function Text({ label, value, onChange, maxLength, hint, invalid, testid }) {
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        data-testid={testid}
        className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17] ${
          invalid ? "border-[#E63946]" : "border-black/15"
        }`}
      />
      {hint ? <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p> : null}
    </label>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="font-display text-sm font-bold">{label}</p>
        {hint ? <p className="mt-0.5 text-xs text-[var(--brand-body)]">{hint}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-[#0A0F17]" : "bg-black/15"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
